import { useState, useEffect, useRef } from 'react';
import { api } from '../api.js';
import { parseDate, formatDateReadable, getTodayMidnight } from '../utils/date.js';

export default function UnitsListPage({ qualification, user, onOpenUnit, onSelectQualification }) {
  const [unitsWithStats, setUnitsWithStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = getTodayMidnight();

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const sortedUnits = [...(qualification.units || [])].sort((a, b) => {
          if (a.activityStartDate && b.activityStartDate) {
            return parseDate(a.activityStartDate).getTime() - parseDate(b.activityStartDate).getTime();
          }
          return 0;
        });

        // Course context = the qualification the student selected on this page
        const courseName = qualification.name;
        const unitsPromises = sortedUnits.map(async (unit) => {
          let stats = null;
          try {
            stats = await api.getUnitStats(unit.code, courseName, user.optionalID || '', user.username || null, controller.signal);
          } catch {}

          const startDate = unit.activityStartDate ? parseDate(unit.activityStartDate) : null;
          const endDate = unit.activityEndDate ? parseDate(unit.activityEndDate) : null;
          const hasStarted = !startDate || startDate <= today;
          const isPastDue = endDate && endDate < today;
          
          return {
            ...unit,
            startDate,
            endDate,
            hasStarted,
            isPastDue,
            available: hasStarted,
            stats,
          };
        });

        setUnitsWithStats(await Promise.all(unitsPromises));
      } catch (err) {
        const sortedUnits = [...(qualification.units || [])].sort((a, b) => {
          if (a.activityStartDate && b.activityStartDate) {
            return parseDate(a.activityStartDate).getTime() - parseDate(b.activityStartDate).getTime();
          }
          return 0;
        });

        setUnitsWithStats(sortedUnits.map(unit => ({
          ...unit,
          startDate: unit.activityStartDate ? parseDate(unit.activityStartDate) : null,
          endDate: unit.activityEndDate ? parseDate(unit.activityEndDate) : null,
          hasStarted: !unit.activityStartDate || parseDate(unit.activityStartDate) <= today,
          isPastDue: unit.activityEndDate && parseDate(unit.activityEndDate) < today,
          available: !unit.activityStartDate || parseDate(unit.activityStartDate) <= today,
          stats: null,
        })));
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [qualification]);

  if (loading) {
    return <div className="card center" style={{ color: '#2c3e50' }}>Loading units...</div>;
  }

  return (
    <div>
      {/* Header */}
      <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>{qualification.name}</h2>

      {user.qualifications.length > 1 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {user.qualifications.map((q, i) => {
            const isActive = q.name === qualification.name;
            return (
              <button
                key={i}
                onClick={() => onSelectQualification(q)}
                style={{
                  padding: '6px 14px',
                  border: `1px solid ${isActive ? 'var(--brand)' : 'var(--border)'}`,
                  borderRadius: 4,
                  background: isActive ? '#f0f4f8' : 'white',
                  color: isActive ? 'var(--brand)' : '#5b6776',
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 400,
                  cursor: 'pointer',
                }}
              >
                {q.name}
              </button>
            );
          })}
        </div>
      )}

      {qualification.commencementDate && (
        <p className="muted" style={{ marginTop: 0, marginBottom: 16 }}>
          Commenced: {formatDateReadable(qualification.commencementDate)}
          {qualification.expectedCompletionDate && ` \u2022 Due: ${formatDateReadable(qualification.expectedCompletionDate)}`}
        </p>
      )}

      {/* Units list */}
      <div className="card" style={{ padding: 0 }}>
        {unitsWithStats.length === 0 ? (
          <p style={{ padding: 24, textAlign: 'center', color: '#2c3e50' }}>No units available for this qualification.</p>
        ) : (
          <div>
            {unitsWithStats.map((unit, idx) => (
              <UnitCard
                key={unit.code}
                unit={unit}
                index={idx}
                total={unitsWithStats.length}
                onOpen={() => onOpenUnit(unit)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UnitCard({ unit, index, total, onOpen }) {
  const hasAttemptsLeft = !unit.stats || (unit.stats.attemptsUsed < unit.stats.maxAttempts);
  const isCompleted = unit.stats && unit.stats.bestScore !== null && unit.stats.bestScore >= 100;
  const allAttemptsExhausted = unit.stats && unit.stats.attemptsUsed >= unit.stats.maxAttempts && unit.stats.bestScore !== null;
  
  function getStatusInfo() {
    if (!unit.stats) {
      // Check if unit opens in the future
      if (unit.startDate && unit.startDate > getTodayMidnight()) {
        return { text: `Opens on: ${formatDateReadable(unit.activityStartDate)}`, color: '#5b6776' };
      }
      return null;
    }
    
    const bestScore = unit.stats.bestScore;
    const used = unit.stats.attemptsUsed || 0;
    const max = unit.stats.maxAttempts || 4;
    
    // Completed with 100%
    if (isCompleted) {
      return { text: 'Completed', color: '#27ae60' };
    }
    
    // All attempts used and failed (<50%)
    if (!hasAttemptsLeft && bestScore !== null && bestScore < 50) {
      return { text: 'Did not pass', color: '#c0392b' };
    }
    
    // All attempts used with partial score (50-99%)
    if (!hasAttemptsLeft && bestScore !== null) {
      const isPassing = bestScore >= 75;
      return { text: `Best attempt: ${bestScore}%`, color: isPassing ? '#27ae60' : '#e67e22' };
    }
    
    // Has attempts left with a score
    if (bestScore !== null && bestScore > 0) {
      const isPassing = bestScore >= 75;
      return { text: `Best: ${bestScore}% • ${used}/${max} attempts`, color: '#5b6776' };
    }
    
    // Has attempts left, no score yet
    if (hasAttemptsLeft) {
      return { text: `${used}/${max} attempts`, color: '#5b6776' };
    }
    
    return null;
  }

  const statusInfo = getStatusInfo();

  // Determine card styling based on state
  let cardStyle = {};
  let titleColor = '#2c3e50';
  
  if (isCompleted || allAttemptsExhausted) {
    cardStyle.opacity = 0.6;
    cardStyle.background = '#f8f9fb';
  } else if (!unit.hasStarted) {
    cardStyle.opacity = 0.5;
    cardStyle.background = '#f8f9fb';
  } else if (unit.isPastDue && hasAttemptsLeft) {
    // Past due but still has attempts - redder tone
    titleColor = '#c0392b';
    cardStyle.opacity = 1;
    cardStyle.background = 'transparent';
  }

  return (
    <>
      <div
        onClick={() => !(!unit.hasStarted || isCompleted || allAttemptsExhausted) && onOpen()}
        style={{
          padding: '16px 20px',
          cursor: (unit.hasStarted && !isCompleted && !allAttemptsExhausted) ? 'pointer' : 'default',
          opacity: cardStyle.opacity,
          background: cardStyle.background,
          transition: 'background 0.1s',
        }}
        onMouseEnter={e => { 
          if (unit.hasStarted && !isCompleted && !allAttemptsExhausted) e.currentTarget.style.background = '#f0f4f8'; 
        }}
        onMouseLeave={e => { 
          if (unit.hasStarted && !isCompleted && !allAttemptsExhausted) e.currentTarget.style.background = cardStyle.background; 
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15, color: titleColor }}>{unit.code}</div>
            
            {/* Status text - Completed/Failed/Best attempt */}
            {statusInfo && (
              <span style={{ fontSize: 12, color: statusInfo.color, fontWeight: 500 }}>
                {statusInfo.text}
              </span>
            )}
          </div>
          
          {/* Due date on the right side */}
          {unit.endDate && (
            <span style={{ 
              fontSize: 12, 
              color: unit.isPastDue ? '#c0392b' : '#5b6776', 
              fontWeight: 500,
              whiteSpace: 'nowrap',
              marginLeft: 16,
            }}>
              Due {formatDateReadable(unit.activityEndDate)}
            </span>
          )}
        </div>
      </div>
      {index < total - 1 && <div style={{ height: 1, background: 'var(--border)', margin: '0 20px' }} />}
    </>
  );
}
